import { useContext } from 'react'
import { useQuery } from '@tanstack/react-query'
import { MyAxios as axios } from 'components/MyAxios'
import { SessionContext } from 'components/session/SessionProvider'
import {
  FITBIT_AUTH_VERSION_LEGACY,
  FITBIT_SERVICE_KEY,
  INTEGRATION_REGISTRATIONS_MINE_PATH,
  INTEGRATION_REGISTRATIONS_PATH
} from 'components/common/Constants'

interface IntegrationRegistration {
  key: string
  authVersion?: string
}

interface Integration {
  key: string
  externalUrl?: string
}

interface LegacyFitbitIntegration {
  hasLegacyFitbit: boolean
  reconnectUrl?: string
}

const STALE_TIME = 5 * 60 * 1000

const registrations = async (sessionToken: string): Promise<IntegrationRegistration[]> => {
  try {
    const response = await axios.get(`${process.env.INTEGRATIONS_URL}${INTEGRATION_REGISTRATIONS_MINE_PATH}`,
      { headers: { Authorization: sessionToken } })
    return Array.isArray(response.data) ? response.data : []
  } catch (e: any) {
    // 404: the user has no registrations yet. 403: svc does not know this
    // session, which for a banner is the same as having no Fitbit.
    if ([403, 404].includes(e?.response?.status)) return []
    throw e
  }
}

const available = async (): Promise<Integration[]> => {
  const response = await axios.get(`${process.env.INTEGRATIONS_URL}${INTEGRATION_REGISTRATIONS_PATH}`)
  return Array.isArray(response.data) ? response.data : []
}

// Whether the current user still has the pre-Google (legacy) Fitbit
// connection, and the url to start the Google based reconnect flow with. The
// registrations live in svc-data-integrations, so they are fetched from there
// directly. The list of available integrations is only fetched once we know we
// need the reconnect url.
const useLegacyFitbitIntegration = (enabled: boolean = true): LegacyFitbitIntegration => {
  const { sessionToken } = useContext(SessionContext)

  const { data: mine } = useQuery<IntegrationRegistration[]>(
    ['integration-registrations-mine'],
    async () => await registrations(sessionToken as string),
    { enabled: enabled && sessionToken !== undefined, staleTime: STALE_TIME, retry: false }
  )

  const hasLegacyFitbit = (mine ?? []).some(registration => (
    registration.key === FITBIT_SERVICE_KEY && registration.authVersion === FITBIT_AUTH_VERSION_LEGACY
  ))

  const { data: integrations } = useQuery<Integration[]>(
    ['integrations-available'],
    async () => await available(),
    { enabled: enabled && hasLegacyFitbit, staleTime: STALE_TIME, retry: false }
  )

  const fitbit = (integrations ?? []).find(integration => integration.key === FITBIT_SERVICE_KEY)

  return { hasLegacyFitbit, reconnectUrl: fitbit?.externalUrl }
}

export default useLegacyFitbitIntegration
